import { ImageEditOptions } from '@sdir/blueprint';
import {
  AttachApplicationAttachmentResult,
  AttachmentType,
  GetApplicationsQueryResult,
} from '@sdir/httpclient/lib/clients/aps/application';
import { BaseTracker, SubmitItem, SubmitStatus } from '../types/submitSequence';
import { makeFilenameUnique, uploadAddedApplicationFile } from './attachmentApiHelpers';
import { addToSequence } from './submitSequenceHelpers';

export type ApplicationImageChange = {
  newCropped: File | null;
  editOptions: ImageEditOptions | null;
  newOriginal: File | null;
  existingGroup: string | null;
};

export type SubmitPhotoAndSignatureTracker = BaseTracker & {
  newPhotoCropped?: SubmitStatus<AttachApplicationAttachmentResult>;
  newPhotoOriginal?: SubmitStatus<AttachApplicationAttachmentResult>;
  newSignatureCropped?: SubmitStatus<AttachApplicationAttachmentResult>;
  newSignatureOriginal?: SubmitStatus<AttachApplicationAttachmentResult>;
};

export const buildSubmitPhotoAndSignatureSequence = ({
  application,
  photo,
  signature,
}: {
  application: GetApplicationsQueryResult;
  photo: ApplicationImageChange | null;
  signature: ApplicationImageChange | null;
}): SubmitItem<SubmitPhotoAndSignatureTracker>[] => {
  const steps: SubmitItem<SubmitPhotoAndSignatureTracker>[] = [];

  const usedFilenames = new Set<string>();
  application.attachments?.forEach((a) => usedFilenames.add(a.blobName || ''));

  if (photo) {
    if (photo.newOriginal) {
      const originalToUpload = makeFilenameUnique(photo.newOriginal, usedFilenames);
      usedFilenames.add(originalToUpload.name);
      addToSequence(steps, {
        labelId: 'photoandsignature.steps.uploadnewphoto',
        apiCall: () =>
          uploadAddedApplicationFile(application.uid || '', originalToUpload, AttachmentType.Photo),
        statusSelector: (ctx) => ctx.newPhotoOriginal,
        statusSetter: (ctx, r) => ({ ...ctx, newPhotoOriginal: r }),
      });
    }

    if (photo.newCropped) {
      const newImageToUpload = makeFilenameUnique(photo.newCropped, usedFilenames);
      usedFilenames.add(newImageToUpload.name);
      addToSequence<SubmitPhotoAndSignatureTracker, AttachApplicationAttachmentResult>(steps, {
        labelId: 'photoandsignature.steps.uploadnewcroppedphoto',
        apiCall: (ctx) =>
          uploadAddedApplicationFile(
            application.uid || '',
            newImageToUpload,
            AttachmentType.PhotoCropped,
            {
              group: ctx.newPhotoOriginal?.result?.group || photo.existingGroup,
              metadata: { imageEditOptions: photo.editOptions },
            }
          ),
        statusSelector: (ctx) => ctx.newPhotoCropped,
        statusSetter: (ctx, r) => ({ ...ctx, newPhotoCropped: r }),
      });
    }
  }

  if (signature) {
    if (signature.newOriginal) {
      const originalToUpload = makeFilenameUnique(signature.newOriginal, usedFilenames);
      usedFilenames.add(originalToUpload.name);
      addToSequence(steps, {
        labelId: 'photoandsignature.steps.uploadnewsignature',
        apiCall: () =>
          uploadAddedApplicationFile(
            application.uid || '',
            originalToUpload,
            AttachmentType.Signature
          ),
        statusSelector: (ctx) => ctx.newSignatureOriginal,
        statusSetter: (ctx, r) => ({ ...ctx, newSignatureOriginal: r }),
      });
    }

    if (signature.newCropped) {
      const newImageToUpload = makeFilenameUnique(signature.newCropped, usedFilenames);
      usedFilenames.add(newImageToUpload.name);
      addToSequence<SubmitPhotoAndSignatureTracker, AttachApplicationAttachmentResult>(steps, {
        labelId: 'photoandsignature.steps.uploadnewcroppedsignature',
        apiCall: (ctx) =>
          uploadAddedApplicationFile(
            application.uid || '',
            newImageToUpload,
            AttachmentType.SignatureCropped,
            {
              group: ctx.newSignatureOriginal?.result?.group || signature.existingGroup,
              metadata: { imageEditOptions: signature.editOptions },
            }
          ),
        statusSelector: (ctx) => ctx.newSignatureCropped,
        statusSetter: (ctx, r) => ({ ...ctx, newSignatureCropped: r }),
      });
    }
  }

  return steps;
};

export const validateImageResolution = (file: File, minWidth = 0, minHeight = 0) => {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      const valid = !(img.naturalWidth < minWidth || img.naturalHeight < minHeight);
      resolve(valid);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };

    img.src = url;
  });
};
